/**
 * 선언적 데이터 검증 코어 — 결과는 `ValidationResult`(throw 대신 수집).
 *
 * phaser-brick `ChapterValidator`의 `{ ok, errors[], warnings[] }` 패턴을 일반화한다.
 * 원시 검증기(`checkRequired`/`checkType`/`checkRange`/`checkEnum`/`checkRef`)와,
 * 시트 스키마 + 시트 간 참조를 **선언**하면 전수 검증하는 `createDataValidator`를 제공한다.
 * 외부 의존 0(zod 등 미사용) — 순수 TS.
 */

export interface ValidationIssue {
  /** 위치 식별자. 예: `"dailyShop[slot0].nameKey"`. */
  readonly path: string;
  /** 위반 규칙. `"required"|"type"|"range"|"enum"|"ref"|"duplicate"`. */
  readonly rule: string;
  /** 사람이 읽을 수 있는 오류 설명. */
  readonly message: string;
}

export interface ValidationResult {
  readonly ok: boolean;
  readonly errors: ValidationIssue[];
  readonly warnings: ValidationIssue[];
}

/** errors가 비면 ok=true. */
export function makeResult(errors: ValidationIssue[], warnings: ValidationIssue[] = []): ValidationResult {
  return { ok: errors.length === 0, errors, warnings };
}

export type PrimitiveType = "int" | "float" | "string" | "bool";

// ─── 원시 검증기 (issue 또는 undefined 반환) ──────────────────────────────────

/**
 * 값이 `undefined`/`null`/빈 문자열이면 `"required"` 이슈를 반환한다. 정상이면 `undefined`.
 */
export function checkRequired(value: unknown, path: string): ValidationIssue | undefined {
  if (value === undefined || value === null || value === "") {
    return { path, rule: "required", message: `필수 값 누락` };
  }
  return undefined;
}

/**
 * 값의 JS 런타임 타입이 `type`과 일치하지 않으면 `"type"` 이슈를 반환한다. 정상이면 `undefined`.
 * `"int"`는 `Number.isInteger`, `"float"`는 `Number.isFinite`로 판정한다.
 */
export function checkType(value: unknown, type: PrimitiveType, path: string): ValidationIssue | undefined {
  const ok =
    type === "int"
      ? typeof value === "number" && Number.isInteger(value)
      : type === "float"
        ? typeof value === "number" && Number.isFinite(value)
        : type === "string"
          ? typeof value === "string"
          : typeof value === "boolean";
  if (!ok) {
    return { path, rule: "type", message: `타입 불일치 — ${type} 기대, 실제 ${JSON.stringify(value)}` };
  }
  return undefined;
}

/**
 * `value`가 `[min, max]` 범위를 벗어나면 `"range"` 이슈를 반환한다. 정상이면 `undefined`.
 * `min`/`max`가 `undefined`이면 해당 방향 검사를 생략한다.
 */
export function checkRange(
  value: number,
  min: number | undefined,
  max: number | undefined,
  path: string,
): ValidationIssue | undefined {
  if (min !== undefined && value < min) {
    return { path, rule: "range", message: `${value} < min ${min}` };
  }
  if (max !== undefined && value > max) {
    return { path, rule: "range", message: `${value} > max ${max}` };
  }
  return undefined;
}

/**
 * `value`가 `members` 목록에 포함되지 않으면 `"enum"` 이슈를 반환한다. 정상이면 `undefined`.
 */
export function checkEnum(value: string, members: readonly string[], path: string): ValidationIssue | undefined {
  if (!members.includes(value)) {
    return { path, rule: "enum", message: `'${value}' ∉ {${members.join(", ")}}` };
  }
  return undefined;
}

/**
 * `value`가 `validKeys`에 없으면 `"ref"` 이슈를 반환한다. 정상이면 `undefined`.
 * @param refName 오류 메시지에 표시할 대상 시트/컬렉션 이름.
 */
export function checkRef(
  value: string,
  validKeys: ReadonlySet<string>,
  refName: string,
  path: string,
): ValidationIssue | undefined {
  if (!validKeys.has(value)) {
    return { path, rule: "ref", message: `참조 '${value}'가 ${refName}에 없음` };
  }
  return undefined;
}

// ─── 선언적 시트 검증 ────────────────────────────────────────────────────────

/**
 * 임의 시트의 한 필드에 선언하는 검증 규칙.
 *
 * `GlobalSchemaEntry`와 **의도적으로 분리된** 별도 타입이다.
 * `FieldRule`은 `createDataValidator`에서 임의 시트의 필드 규칙(type·required·range·enum)을
 * 선언하는 데 쓰이고, `GlobalSchemaEntry`는 global.tsv 전용 type+range 스키마로 사용된다.
 * 두 타입은 SSoT 중복이 아니라 사용 범위가 다르다.
 */
export interface FieldRule {
  /** 기대 원시 타입. 지정 시 불일치하면 `"type"` 이슈를 생성한다. */
  readonly type?: PrimitiveType;
  /** `true`이면 값이 `undefined`/`null`/빈 문자열일 때 `"required"` 이슈를 생성한다. */
  readonly required?: boolean;
  /** 수치 하한. 값이 `min` 미만이면 `"range"` 이슈. `type`이 `"int"` 또는 `"float"`일 때만 의미 있다. */
  readonly min?: number;
  /** 수치 상한. 값이 `max` 초과이면 `"range"` 이슈. `type`이 `"int"` 또는 `"float"`일 때만 의미 있다. */
  readonly max?: number;
  /** 허용 문자열 목록. 값이 목록에 없으면 `"enum"` 이슈. `type`이 `"string"`일 때만 적용된다. */
  readonly enum?: readonly string[];
}

/**
 * 한 시트의 행이 다른 시트의 키를 참조하는 교차 참조 규칙.
 * `values`가 행에서 참조 키들을 추출하고, `in`이 대상 시트 이름을 지정한다.
 */
export interface RefRule<T> {
  /** 오류 경로(`sheet[key].field`)에 표시할 필드명. */
  readonly field: string;
  /** 행 → 해당 행이 참조하는 키 목록을 반환하는 함수. */
  readonly values: (row: T) => readonly string[];
  /** 참조 대상 시트의 `SheetSpec.name`. 해당 시트가 선언되지 않으면 오류. */
  readonly in: string;
}

/** `createDataValidator`에 전달하는 단일 시트 선언. */
export interface SheetSpec<T = Record<string, unknown>> {
  /** 오류 경로에 사용할 고유 시트 이름. `RefRule.in`이 이 값을 참조한다. */
  readonly name: string;
  /** 검증 대상 행 배열. */
  readonly rows: readonly T[];
  /** 행에서 고유 키를 추출하는 함수. 키 중복 검사에 사용된다. */
  readonly keyOf: (row: T) => string;
  /** 필드별 규칙(필드명 → {@link FieldRule}). 지정되지 않은 필드는 검증 생략. */
  readonly fields?: Readonly<Record<string, FieldRule>>;
  /** 다른 시트 키를 참조하는 교차 참조 규칙 목록. */
  readonly refs?: readonly RefRule<T>[];
}

/**
 * 여러 시트를 선언하면 ① 키 중복 ② 필드 규칙(required/type/range/enum) ③ 시트 간 참조를 전수 검증한다.
 * 새 시트/참조는 SheetSpec 1개 추가로 커버 — `dataIntegrity.test.ts`의 하드코딩 cross-check를 점진 대체.
 */
export function createDataValidator(sheets: readonly SheetSpec<unknown>[]): { validateAll(): ValidationResult } {
  return {
    validateAll(): ValidationResult {
      const errors: ValidationIssue[] = [];
      const warnings: ValidationIssue[] = [];

      // 1차 — 시트별 키 집합 + 중복 검사
      const keysBySheet = new Map<string, Set<string>>();
      for (const sheet of sheets) {
        const keys = new Set<string>();
        for (const row of sheet.rows) {
          const k = sheet.keyOf(row);
          if (keys.has(k)) {
            errors.push({ path: `${sheet.name}[${k}]`, rule: "duplicate", message: `중복 키 '${k}'` });
          }
          keys.add(k);
        }
        keysBySheet.set(sheet.name, keys);
      }

      // 2차 — 필드 규칙 + 참조
      for (const sheet of sheets) {
        for (const row of sheet.rows) {
          const rowKey = sheet.keyOf(row);
          const rec = row as Record<string, unknown>;

          if (sheet.fields) {
            for (const [field, rule] of Object.entries(sheet.fields)) {
              const path = `${sheet.name}[${rowKey}].${field}`;
              const val = rec[field];
              if (rule.required) {
                const req = checkRequired(val, path);
                if (req) {
                  errors.push(req);
                  continue;
                }
              }
              if (val === undefined || val === null) continue; // optional 미존재
              if (rule.type) {
                const t = checkType(val, rule.type, path);
                if (t) errors.push(t);
              }
              if (rule.enum && typeof val === "string") {
                const e = checkEnum(val, rule.enum, path);
                if (e) errors.push(e);
              }
              if ((rule.min !== undefined || rule.max !== undefined) && typeof val === "number") {
                const r = checkRange(val, rule.min, rule.max, path);
                if (r) errors.push(r);
              }
            }
          }

          if (sheet.refs) {
            for (const ref of sheet.refs) {
              const target = keysBySheet.get(ref.in);
              const path = `${sheet.name}[${rowKey}].${ref.field}`;
              if (!target) {
                errors.push({ path, rule: "ref", message: `대상 시트 '${ref.in}' 미선언` });
                continue;
              }
              for (const v of ref.values(row)) {
                const issue = checkRef(v, target, ref.in, path);
                if (issue) errors.push(issue);
              }
            }
          }
        }
      }

      return makeResult(errors, warnings);
    },
  };
}
